import React, { useState, useEffect } from 'react';

import moment from 'moment';

import { Modal, Radio } from '@material-ui/core';
import Rating from '@material-ui/lab/Rating';

import { Autorenew as LoadingIcon } from '@material-ui/icons';

import { submitProjectEvaluation } from '../../../services/apiService';
import { RefreshCounterContext } from '../index';

import RenderProfilePic from '../../../extras/RenderProfilePic';

const timeFormat = 'DD MMM YYYY hh:mm:ss a';

export default function AdminFeedbackModal({
  params,
  openModal,
  setOpenModal,
}) {
  const setRefreshCounterFn = React.useContext(RefreshCounterContext);

  const [rating, setRating] = useState([]);
  const [fixedRating, setFixedRating] = useState([]);

  const [plaigarism, setPlaigarism] = useState('');
  const [feedbackText, setFeedbackText] = useState('');

  const [loading, setLoading] = useState(false);

  const [isRatingDecreaseError, setIsRatingDecreaseError] = useState(false);
  const [isSubmitError, setIsSubmitError] = useState(false);

  useEffect(() => {
    const feedbackObj = params?.data?.coachFeedback;

    const allRubricsArr =
      params.data?.project?.attributes?.project_category?.data?.attributes
        ?.rubrics;

    setFeedbackText(feedbackObj?.feedback);

    const rubricsValue = feedbackObj?.ratings;

    if (!allRubricsArr) return;

    for (let i = 0; i < allRubricsArr?.length; i++) {
      const rubricItem = allRubricsArr[i];
      const rubricItemKey = rubricItem.rubricKey;

      if (rubricsValue && rubricsValue[rubricItemKey]) {
        rubricItem.value = rubricsValue[rubricItemKey];
      } else {
        rubricItem.value = 0;
      }
    }

    if (feedbackObj?.plagiarisedSolution) {
      setPlaigarism('plagiarised');
    } else {
      setPlaigarism('self_written');
    }

    setRating(allRubricsArr);
    setFixedRating(JSON.parse(JSON.stringify(allRubricsArr)));
  }, []);

  const onSubmit = async () => {
    setIsSubmitError(false);

    const ratingObj = {};

    for (let i = 0; i < rating.length; i++) {
      const item = rating[i];

      if (!item.value) {
        return;
      }

      ratingObj[item.rubricKey] = item.value;
    }

    setLoading(true);

    const body = {
      studentId: params?.data?.userId,
      evaluatorUserId: params?.data?.evaluatorUserId,
      coachFeedback: {
        ratings: ratingObj,
        feedback: feedbackText,
        plagiarisedSolution: plaigarism === 'plagiarised',
      },
    };

    try {
      await submitProjectEvaluation(params?.data?.project?.id, body);
      setLoading(false);
      setOpenModal(false);
      setRefreshCounterFn((counter) => counter + 1);
    } catch (error) {
      console.error(error);
      setLoading(false);
      setIsSubmitError(true);
    }
  };

  const handlePlaigarismCLick = (event) => {
    setPlaigarism(event.target.value);
  };

  return (
    <Modal
      open={openModal}
      aria-labelledby='modal-modal-title'
      aria-describedby='modal-modal-description'
    >
      <div className='relative overflow-y-auto h-5/6 top-2 md:top-10 m-auto bg-white  rounded-lg md:px-8 px-3 py-4 z-50 lg:w-1/2 w-11/12 border-solid border border-gray-600'>
        <div
          className='absolute right-2 top-2 cursor-pointer'
          onClick={() => {
            setOpenModal(false);
          }}
        >
          X
        </div>
        <div className='flex flex-col'>
          <div className='flex mb-4'>
            {params.data?.project?.attributes?.icon?.data?.attributes?.url ? (
              <img
                src={
                  params.data?.project?.attributes?.icon?.data?.attributes?.url
                }
                className='w-16 h-16 rounded-full'
              />
            ) : (
              <RenderProfilePic
                name={params.data?.project?.attributes?.name}
                thumbnail=''
                size='3'
              />
            )}

            <div className='flex-auto'>
              <div className='mr-2 px-4 flex items-center gap-3'>
                <div className='flex-auto'>
                  <h3 className='font-medium'>
                    {params.data?.project?.attributes?.name}
                  </h3>

                  <span className='text-gray-600 text-opacity-80 text-sm'>
                    {
                      params.data?.project?.attributes?.project_category?.data
                        ?.attributes?.name
                    }
                  </span>
                </div>
                <div>
                  <div className='text-gray-500 text-sm grow-0'>
                    Submitted on{' '}
                    {moment(params?.data?.submittedAt).format(timeFormat)}
                  </div>
                  {params?.data?.evaluatedAt && (
                    <div className='text-gray-500 text-sm grow-0'>
                      Evaluated on{' '}
                      {moment(params?.data?.evaluatedAt).format(timeFormat)}
                    </div>
                  )}
                </div>
              </div>

              <div className='flex items-center px-4 mt-3 rounded w-max text-sm'>
                <div className='font-medium mr-5'>By</div>
                <RenderProfilePic
                  thumbnail={params?.data?.Students?.thumbnail}
                  name={params?.data?.Students?.firstName}
                  size={3}
                />
                <div className='ml-2'>{`${params.data?.Students?.firstName} ${params.data?.Students?.lastName}`}</div>
              </div>
            </div>
          </div>
          <hr className='mb-4' />
          <div className='font-bold'>Ratings & Feedback</div>

          {rating.map((rubricItem, index) => (
            <div className='flex mb-2' key={index}>
              <div className='w-64 text-gray-400 text-sm'>
                {rubricItem.rubricName} {`(${rubricItem.rubricWeight})`}
              </div>

              <Rating
                max={10}
                name={rubricItem.rubricName}
                value={rubricItem.value}
                onChange={(event, newValue) => {
                  setIsRatingDecreaseError(false);
                  if (!newValue) return;
                  const newRating = [...rating];
                  const oldValue = fixedRating[index].value;
                  if (newValue < oldValue) {
                    setIsRatingDecreaseError(true);
                    return;
                  }
                  newRating[index].value = newValue;

                  setRating(newRating);
                }}
              />
            </div>
          ))}
          {isRatingDecreaseError && (
            <span className='text-red-700 text-sm'>
              Ratings cannot be decreased!
            </span>
          )}

          <hr className='mt-4 mb-4' />

          <div className='text-gray-400 text-sm'>Plaigarism</div>
          <div className='flex items-center -mb-2 -ml-2'>
            <Radio
              color='primary'
              name='plaigarism'
              value='self_written'
              checked={plaigarism === 'self_written'}
              onChange={handlePlaigarismCLick}
            />
            <span className='text-sm font-normal opacity-60'>
              Self-written submission
            </span>
          </div>
          <div className='flex items-center -mb-2 -ml-2'>
            <Radio
              color='primary'
              name='plaigarism'
              value='plagiarised'
              checked={plaigarism === 'plagiarised'}
              onChange={handlePlaigarismCLick}
            />
            <span className='text-sm font-normal opacity-60'>
              Plagiarised submission
            </span>
          </div>

          <div className='text-sm text-gray-400 mt-4'>Feedback</div>
          <textarea
            disabled={true}
            rows='4'
            className='w-full border border-gray-300 focus:outline-none focus:border-blue-c1 focus:ring-1 focus:ring-blue-c1 rounded-lg p-2'
            value={feedbackText}
          />

          <div className='flex flex-col items-center justify-center  my-4'>
            {isSubmitError && (
              <span className='text-red-700 text-sm mt-1'>
                Failed to submit. Please try again.
              </span>
            )}

            <div className='flex justify-center'>
              <button
                className='w-max bg-red-700 py-2 px-10 my-1 font-medium text-white text-sm rounded focus:outline-none'
                disabled={loading}
                onClick={onSubmit}
              >
                {loading ? (
                  <LoadingIcon className='animate-spin' />
                ) : (
                  'Submit Feedback'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}
